import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export const SUPPORTED_PLATFORM_CURRENCIES = [
  "SAR", "AED", "USD", "EUR", "KWD", "BHD", "QAR", "OMR", "EGP", "JOD",
] as const;

export type PlatformCurrency = typeof SUPPORTED_PLATFORM_CURRENCIES[number];

export const CURRENCY_LABELS: Record<PlatformCurrency, { ar: string; en: string; symbol: string }> = {
  SAR: { ar: "ريال سعودي", en: "Saudi Riyal",        symbol: "ر.س" },
  AED: { ar: "درهم إماراتي", en: "UAE Dirham",       symbol: "د.إ" },
  USD: { ar: "دولار أمريكي", en: "US Dollar",        symbol: "$" },
  EUR: { ar: "يورو",         en: "Euro",             symbol: "€" },
  KWD: { ar: "دينار كويتي",  en: "Kuwaiti Dinar",    symbol: "د.ك" },
  BHD: { ar: "دينار بحريني", en: "Bahraini Dinar",   symbol: "د.ب" },
  QAR: { ar: "ريال قطري",    en: "Qatari Riyal",     symbol: "ر.ق" },
  OMR: { ar: "ريال عماني",   en: "Omani Riyal",      symbol: "ر.ع" },
  EGP: { ar: "جنيه مصري",    en: "Egyptian Pound",   symbol: "ج.م" },
  JOD: { ar: "دينار أردني",  en: "Jordanian Dinar",  symbol: "د.أ" },
};

const STORAGE_KEY = "dwrrah_platform_currency";
const FALLBACK: PlatformCurrency = "SAR";

interface Ctx {
  currency: PlatformCurrency;
  setCurrency: (c: PlatformCurrency) => void;
  isManualOverride: boolean;
  source: "manual" | "stored" | "ip" | "fallback" | "loading";
  loading: boolean;
}

const PlatformCurrencyContext = createContext<Ctx>({
  currency: FALLBACK,
  setCurrency: () => {},
  isManualOverride: false,
  source: "loading",
  loading: true,
});

function isSupported(c: any): c is PlatformCurrency {
  return typeof c === "string" && (SUPPORTED_PLATFORM_CURRENCIES as readonly string[]).includes(c);
}

export function PlatformCurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<PlatformCurrency>(FALLBACK);
  const [source, setSource] = useState<Ctx["source"]>("loading");
  const [loading, setLoading] = useState(true);
  const [isManual, setIsManual] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function detect() {
      // Priority 1: localStorage (manual choice)
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isSupported(stored)) {
          if (!cancelled) {
            setCurrencyState(stored);
            setIsManual(true);
            setSource("stored");
            setLoading(false);
          }
          return;
        }
      } catch {}

      // Priority 2: IP detection
      try {
        const { data } = await supabase.functions.invoke("detectGeoCurrency");
        if (!cancelled && data?.currency && isSupported(data.currency)) {
          setCurrencyState(data.currency);
          setSource(data.source === "fallback" ? "fallback" : "ip");
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn("detectGeoCurrency failed:", e);
      }

      // Priority 3: SAR fallback
      if (!cancelled) {
        setCurrencyState(FALLBACK);
        setSource("fallback");
        setLoading(false);
      }
    }

    detect();
    return () => { cancelled = true; };
  }, []);

  const setCurrency = useCallback((c: PlatformCurrency) => {
    if (!isSupported(c)) return;
    try { localStorage.setItem(STORAGE_KEY, c); } catch {}
    setCurrencyState(c);
    setIsManual(true);
    setSource("manual");
  }, []);

  const value = useMemo(
    () => ({ currency, setCurrency, isManualOverride: isManual, source, loading }),
    [currency, setCurrency, isManual, source, loading]
  );

  return (
    <PlatformCurrencyContext.Provider value={value}>
      {children}
    </PlatformCurrencyContext.Provider>
  );
}

export function usePlatformCurrency() {
  return useContext(PlatformCurrencyContext);
}

export function formatPlatformPrice(amount: number, currency: PlatformCurrency, lang: "ar" | "en" = "ar"): string {
  const label = CURRENCY_LABELS[currency];
  const sym = label?.symbol || currency;
  // Decimals: KWD/BHD/OMR use 3 typically, others 2 — but for whole-unit subscription pricing we keep clean
  const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return lang === "ar" ? `${formatted} ${sym}` : `${sym} ${formatted}`;
}